import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Header from '../../../components/feature/Header';
import Footer from '../../../components/feature/Footer';

export default function WorkflowSolutionsPage() {
  const [selectedImage, setSelectedImage] = useState<number | null>(null);
  const [showPdfModal, setShowPdfModal] = useState(false);

  useEffect(() => {
    // Update document title and meta tags
    document.title = "Workflow Solutions for Multi-Brand Operations - Case Study | Esteban's Stache";
    
    // Update meta description
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Comprehensive workflow systems designed to optimize creative operations across volume, speed, quality, digital and print requirements for diverse brand portfolios with 75% reduction in turnaround times.');
    }
    
    // Update canonical URL
    const canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) {
      canonical.setAttribute('href', '/projects/workflow-solutions');
    }

    // Scroll to top when page loads
    window.scrollTo(0, 0);
  }, []);

  const project = {
    title: 'Workflow Solutions for Multi-Brand Operations',
    category: 'Process Design',
    description: 'Comprehensive workflow systems designed to optimize creative operations across volume, speed, quality, digital and print requirements for diverse brand portfolios.',
    image: 'https://static.readdy.ai/image/3fdc746f0d1d84f2cde4df046cd392e8/5c34a20aad27b2abf7cf81b7761be343.jpeg',
    challenge: 'Each brand partner required specialized workflows to meet their unique operational demands - from high-volume social content to premium print materials, while maintaining consistent quality and efficiency across all touchpoints.',
    solution: 'I developed five modular workflow frameworks—Volume for high-output social campaigns, Speed for real-time activations, Quality for premium deliverables, Digital for online platforms, and Print for physical materials—that can function independently or seamlessly together. By combining them, teams can produce large quantities quickly, refine hero assets with polish, and deliver optimized files across both digital and print channels with consistent, efficient results.',
    results: [
      '75% reduction in project turnaround times',
      '40% increase in team productivity across all brands',
      '90% decrease in revision cycles through clear process documentation',
      'Standardized quality control across 12+ brand partnerships',
      '50% improvement in cross-team collaboration efficiency'
    ],
    gallery: [
      'https://public.readdy.ai/ai/img_res/d432f06d-748a-4df6-a314-6f7d5ab4fa7c.jpg',
      'https://public.readdy.ai/ai/img_res/9f00fd31ffd1acdf1975deb5a484ddf5.jpg',
      'https://readdy.ai/api/search-image?query=Digital%20and%20Print%20workflow%20comparison%20infographic%2C%20multi-channel%20production%20process%2C%20cross-platform%20workflow%20design%2C%20digital-first%20creative%20operations%2C%20print%20production%20workflow%2C%20integrated%20creative%20systems&width=800&height=600&seq=workflow-digital-print&orientation=landscape'
    ]
  };

  const galleryImages = [
    {
      src: 'https://static.readdy.ai/image/3fdc746f0d1d84f2cde4df046cd392e8/08c1a53af40b00e27eacc645c3f76662.jpeg',
      alt: 'We have developed a variety of workflows customized to the goals of each event',
      type: 'image'
    },
    {
      src: 'https://static.readdy.ai/image/3fdc746f0d1d84f2cde4df046cd392e8/5c34a20aad27b2abf7cf81b7761be343.jpeg',
      alt: 'I helped develop a digital workflow solution that prioritizes immediate engagement with the ability to collect data post-photo interaction. This allows for greater efficiency at high-volume events.',
      type: 'image'
    },
    {
      src: 'https://drive.google.com/file/d/1vxiHBRCTvBoZ9gRmXaCHdTKtsBhHvno2/preview',
      alt: 'Workflow Solutions Documentation - Detailed process and implementation guide',
      type: 'pdf'
    }
  ];

  const handlePrevImage = () => {
    if (selectedImage !== null) {
      setSelectedImage(selectedImage === 0 ? galleryImages.length - 1 : selectedImage - 1);
    }
  };

  const handleNextImage = () => {
    if (selectedImage !== null) {
      setSelectedImage(selectedImage === galleryImages.length - 1 ? 0 : selectedImage + 1);
    }
  };

  const handleKeyDown = (e: KeyboardEvent) => {
    if (selectedImage === null) return;
    if (e.key === 'Escape') setSelectedImage(null);
    if (e.key === 'ArrowLeft') handlePrevImage();
    if (e.key === 'ArrowRight') handleNextImage();
  };

  useEffect(() => {
    if (selectedImage !== null) {
      document.addEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'unset';
    };
  }, [selectedImage]);

  return (
    <div className="min-h-screen bg-white">
      <Header />
      {/* Header */}
      <div className="bg-gradient-to-r from-teal-50 to-blue-50 py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center mb-6">
            <Link 
              to="/work" 
              className="flex items-center text-teal-600 hover:text-teal-700 font-medium transition-colors duration-200"
            >
              <i className="ri-arrow-left-line mr-2"></i>
              Back to Portfolio
            </Link>
          </div>
          <div>
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">{project.title}</h1>
            <p className="text-xl text-teal-600 font-medium">{project.category}</p>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        {/* Hero Image */}
        <div className="relative h-96 md:h-[500px] rounded-2xl overflow-hidden mb-16">
          <img
            src={project.image}
            alt={project.title}
            className="w-full h-full object-cover object-top"
          />
        </div>

        {/* Project Overview */}
        <div className="mb-16">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">Project Overview</h2>
          <p className="text-lg text-gray-600 leading-relaxed">
            {project.description}
          </p>
        </div>

        {/* Project Details */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 mb-16">
          <div>
            <h3 className="text-2xl font-bold text-gray-900 mb-6">The Challenge</h3>
            <p className="text-gray-600 leading-relaxed mb-8">
              {project.challenge}
            </p>

            <h3 className="text-2xl font-bold text-gray-900 mb-6">Our Solution</h3>
            <p className="text-gray-600 leading-relaxed">
              {project.solution}
            </p>
          </div>

          <div>
            <h3 className="text-2xl font-bold text-gray-900 mb-6">Results Achieved</h3>
            <div className="space-y-4">
              {project.results.map((result, index) => (
                <div key={index} className="flex items-start">
                  <div className="w-6 h-6 flex items-center justify-center bg-teal-100 rounded-full mr-4 mt-0.5">
                    <i className="ri-check-line text-teal-600 text-sm"></i>
                  </div>
                  <span className="text-gray-700 font-medium">{result}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Gallery Section */}
        <section className="py-20 px-6">
          <div className="max-w-7xl mx-auto">
            <h2 className="text-4xl font-bold text-gray-900 mb-12 text-center">Project Gallery</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {galleryImages.map((image, index) => (
                <div 
                  key={index} 
                  className="group relative overflow-hidden rounded-lg shadow-lg aspect-[4/3] cursor-pointer"
                  onClick={() => {
                    if (image.type === 'pdf') {
                      setShowPdfModal(true);
                    } else {
                      setSelectedImage(index);
                    }
                  }}
                >
                  {image.type === 'pdf' ? (
                    <div className="w-full h-full bg-gradient-to-br from-teal-50 to-blue-50 flex flex-col items-center justify-center">
                      <i className="ri-file-pdf-line text-teal-600 text-6xl mb-4"></i>
                      <p className="text-gray-700 font-semibold text-center px-4">Workflow Documentation</p>
                      <p className="text-gray-500 text-sm mt-2">Click to view PDF</p>
                    </div>
                  ) : (
                    <img
                      src={image.src}
                      alt={image.alt}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300"
                    />
                  )}
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-all duration-300 flex items-center justify-center">
                    <i className={`${image.type === 'pdf' ? 'ri-eye-line' : 'ri-search-line'} text-white text-4xl opacity-0 group-hover:opacity-100 transition-opacity duration-300`}></i>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Image Lightbox Modal */}
        {selectedImage !== null && galleryImages[selectedImage].type === 'image' && (
          <div 
            className="fixed inset-0 bg-black/95 z-50 flex items-center justify-center p-6"
            onClick={() => setSelectedImage(null)}
          >
            <button
              onClick={() => setSelectedImage(null)}
              className="absolute top-6 right-6 text-white hover:text-gray-300 transition-colors z-10 w-12 h-12 flex items-center justify-center"
              aria-label="Close lightbox"
            >
              <i className="ri-close-line text-4xl"></i>
            </button>

            <button
              onClick={(e) => {
                e.stopPropagation();
                handlePrevImage();
              }}
              className="absolute left-6 text-white hover:text-gray-300 transition-colors z-10 w-12 h-12 flex items-center justify-center"
              aria-label="Previous image"
            >
              <i className="ri-arrow-left-s-line text-5xl"></i>
            </button>

            <button
              onClick={(e) => {
                e.stopPropagation();
                handleNextImage();
              }}
              className="absolute right-6 text-white hover:text-gray-300 transition-colors z-10 w-12 h-12 flex items-center justify-center"
              aria-label="Next image"
            >
              <i className="ri-arrow-right-s-line text-5xl"></i>
            </button>

            <div 
              className="flex items-center gap-8 max-w-7xl w-full h-full px-20"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex-1 h-full flex items-center justify-center">
                <img
                  src={galleryImages[selectedImage].src}
                  alt={galleryImages[selectedImage].alt}
                  className="max-w-full max-h-full object-contain"
                />
              </div>
              <div className="w-80 flex flex-col justify-center">
                <p className="text-white text-lg leading-relaxed mb-4">
                  {galleryImages[selectedImage].alt}
                </p>
                <p className="text-gray-400 text-sm">
                  {selectedImage + 1} / {galleryImages.length}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* PDF Modal */}
        {showPdfModal && (
          <div 
            className="fixed inset-0 bg-black/95 z-50 flex items-center justify-center p-6"
            onClick={() => setShowPdfModal(false)}
          >
            <button
              onClick={() => setShowPdfModal(false)}
              className="absolute top-6 right-6 text-white hover:text-gray-300 transition-colors z-10 w-12 h-12 flex items-center justify-center"
              aria-label="Close PDF viewer"
            >
              <i className="ri-close-line text-4xl"></i>
            </button>

            <div 
              className="w-full h-full max-w-6xl flex flex-col"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="bg-white/10 backdrop-blur-sm rounded-t-lg px-6 py-4 mb-2">
                <h3 className="text-white text-xl font-semibold">Workflow Solutions Documentation</h3>
              </div>
              <div className="flex-1 bg-white rounded-b-lg overflow-hidden">
                <iframe
                  src="https://drive.google.com/file/d/1vxiHBRCTvBoZ9gRmXaCHdTKtsBhHvno2/preview"
                  className="w-full h-full"
                  allow="autoplay"
                  title="Workflow Solutions PDF"
                ></iframe>
              </div>
            </div>
          </div>
        )}

        {/* CTA */}
        {/* Removed CTA section */}
      </div>
      <Footer />
    </div>
  );
}
